/**
 * OfflineStatusIndicator Component
 * Shows network status and pending offline submissions
 */
import { useState, useEffect } from 'react';
import { Wifi, WifiOff, Cloud, CloudOff, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { offlineSync, offlineFormService } from '../../services/offlineSync';

const OfflineStatusIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [formStatus, setFormStatus] = useState({ pending: 0, byType: {} });

  useEffect(() => {
    // Initialize
    offlineSync.init().catch(console.error);
    updateStatus();

    // Listen for events
    const unsubscribe = offlineSync.addListener((event) => {
      if (event.type === 'online') {
        setIsOnline(true);
        updateStatus();
      } else if (event.type === 'offline') {
        setIsOnline(false);
      } else if (event.type.includes('form') || event.type.includes('sync')) {
        updateStatus();
        if (event.type === 'sync_started') setIsSyncing(true);
        if (event.type === 'sync_complete' || event.type === 'sync_error') setIsSyncing(false);
      }
    });

    const handleOnline = () => { setIsOnline(true); updateStatus(); };
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Poll for updates every 30 seconds
    const interval = setInterval(updateStatus, 30000);

    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  const updateStatus = async () => {
    try {
      const syncStatus = await offlineSync.getStatus();
      setPendingCount(syncStatus.pendingChanges);

      const formSyncStatus = await offlineFormService.getFormSyncStatus();
      setFormStatus(formSyncStatus);
    } catch (error) {
      console.error('Error updating offline status:', error);
    }
  };

  const handleSync = async () => {
    if (!isOnline) return;
    
    setIsSyncing(true);
    try {
      await offlineFormService.syncFormSubmissions();
      await offlineSync.sync();
      await updateStatus();
    } finally {
      setIsSyncing(false);
    }
  };

  const totalPending = pendingCount + formStatus.pending;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button 
          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
            isOnline 
              ? totalPending > 0 
                ? 'bg-amber-500/10 text-amber-600 hover:bg-amber-500/20' 
                : 'bg-green-500/10 text-green-600 hover:bg-green-500/20'
              : 'bg-red-500/10 text-red-600 hover:bg-red-500/20'
          }`}
          data-testid="offline-status-indicator"
        >
          {isOnline ? (
            <>
              {totalPending > 0 ? (
                <Cloud className="h-4 w-4" />
              ) : (
                <Wifi className="h-4 w-4" />
              )}
              <span className="text-sm font-medium hidden sm:inline">
                {totalPending > 0 ? `${totalPending} pending` : 'Online'}
              </span>
            </>
          ) : (
            <>
              <WifiOff className="h-4 w-4" />
              <span className="text-sm font-medium hidden sm:inline">Offline</span>
            </>
          )}
          {totalPending > 0 && (
            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
              {totalPending}
            </Badge>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="end">
        <div className="space-y-4">
          {/* Status Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isOnline ? (
                <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              ) : (
                <div className="h-2 w-2 rounded-full bg-red-500" />
              )}
              <span className="font-medium">
                {isOnline ? 'Connected' : 'Offline Mode'}
              </span>
            </div>
            {isOnline && totalPending > 0 && (
              <Button 
                size="sm" 
                variant="outline" 
                onClick={handleSync}
                disabled={isSyncing}
                className="h-7"
              >
                <RefreshCw className={`h-3 w-3 mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
                Sync
              </Button>
            )}
          </div>

          {/* Offline Message */}
          {!isOnline && (
            <p className="text-sm text-muted-foreground">
              Your changes are being saved locally and will sync automatically when you're back online.
            </p>
          )}

          {/* Pending Items Breakdown */}
          {totalPending > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Pending Sync</h4>
              <div className="space-y-1.5">
                {formStatus.byType.work_orders > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Work Orders</span>
                    <Badge variant="outline">{formStatus.byType.work_orders}</Badge>
                  </div>
                )}
                {formStatus.byType.inspections > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Inspections</span>
                    <Badge variant="outline">{formStatus.byType.inspections}</Badge>
                  </div>
                )}
                {formStatus.byType.assets > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Assets</span>
                    <Badge variant="outline">{formStatus.byType.assets}</Badge>
                  </div>
                )}
                {pendingCount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Other Changes</span>
                    <Badge variant="outline">{pendingCount}</Badge>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Failed Submissions Warning */}
          {formStatus.failedSubmissions > 0 && (
            <div className="flex items-start gap-2 p-2 bg-red-50 rounded-md">
              <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-red-700">
                  {formStatus.failedSubmissions} submission(s) failed
                </p>
                <p className="text-red-600 text-xs">
                  Please check your connection and try again
                </p>
              </div>
            </div>
          )}

          {/* All Synced Message */}
          {isOnline && totalPending === 0 && (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <Cloud className="h-4 w-4" />
              <span>All changes synced</span>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default OfflineStatusIndicator;
